import AddIcon from '@mui/icons-material/Add';
import { Box, Snackbar, TextField } from '@mui/material';
import { useIsMutating } from '@tanstack/react-query';
import _ from 'lodash';
import qs from 'qs';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AccordionSection from '../components/AccordionSection';
import AccountAddressTable from '../components/accounts/AccountAddressTable';
import AccountContactTable from '../components/accounts/AccountContactTable';
import AccountTaskTable from '../components/accounts/AccountTaskTable/AccountTaskTable';
import ActivityLogsSection from '../components/activityLogs/ActivityLogsSection';
import AttachmentsSection from '../components/attachments/AttachmentsSection';
import EmployeeSearchAutocomplete from '../components/common/EmployeeSearchAutocomplete/EmployeeSearchAutocomplete';
import MoreOptions from '../components/common/MoreOptions/MoreOptions';
import TagsSection from '../components/common/TagsSection/TagsSection';
import TextFieldWithLink from '../components/common/TextFieldWithLink/TextFieldWithLink';
import EventsTimeline from '../components/common/eventTimeline/EventsTimeline';
import ItemHeader from '../components/common/ItemHeader/ItemHeader';
import NotesSection from '../components/notes/NotesSection';
import { DisableContext } from '../contexts/DisableContextProvider';
import { ROUTE_PATHS } from '../routes/Routes';
import { useCreateAccount, useGetAccountById, useUpdateAccount } from '../services/react-query/accounts';
import { useArchiveAccount } from '../services/react-query/accounts/useArchiveEvent';
import { useGetAllContacts } from '../services/react-query/contacts/useGetAllContacts';
import { useGetAllEmployees } from '../services/react-query/employees';
import { mutationKeys } from '../services/react-query/mutationKeys';
import { useGetAllPreferences } from '../services/react-query/preferences';
import {
  inputContainerStyling,
  inputDividerStyling,
  itemActivitySectionStyling,
  itemContainerStyling,
  itemInnerLayoutStyling,
  itemWrapperStyling,
} from '../styles/sxStyling';
import { Account, AccountSaveResponse } from '../types/Account';
import { ActivityLogOwnerType } from '../types/ActivityLog';
import { Address } from '../types/Address';
import { AttachmentOwnerType } from '../types/Attachment';
import { ContactItemQueryParams } from '../types/Contact';
import { NoteableType } from '../types/Note';
import { Preference, PREFERENCES_VALID_KEYS } from '../types/Preference';
import { UserConfigurableModule, UserSettingsCollapseKeys } from '../types/User';
import { Loading } from '../uikit';
import ResponsiveButton from '../uikit/buttons/ResponsiveButton';
import { errorPopulator, uuidFinder } from '../utility/inputUtils';
import { AccountCircleOutlined } from '@mui/icons-material';
import PreferencePicker from '../components/preferences/picker/PreferencePicker';
import usePermissionAllowed from '../hooks/usePermissionAllowed';
import { Actions } from '../services/react-query/permissions';

const AccountItem: React.FC = () => {
  const { uuid } = useParams();
  const isNew = uuid === 'new';
  const navigate = useNavigate();

  const [account, setAccount] = useState<Account | null>(null);
  const [saved, setSaved] = useState<boolean>(false);
  const [addresses, setAddresses] = useState<Address[]>([]);

  // Data hooks
  const { data: accountData, isLoading: isGetAccountByIdLoading } = useGetAccountById({ uuid: _.toString(uuid) });

  const { data: preferencesData, isFetching: isGetAllPreferencesFetching } = useGetAllPreferences({
    params: {
      filters: {
        keys: [PREFERENCES_VALID_KEYS.ACCOUNT_SCHOOL_DISTRICT, PREFERENCES_VALID_KEYS.ACCOUNT_STATUS, PREFERENCES_VALID_KEYS.ACCOUNT_TYPE],
      },
    },
  });

  const { data: contactData } = useGetAllContacts({
    params: { per_page: 0, filters: { account: uuid } },
  });

  const { data: employeeData } = useGetAllEmployees({ params: { per_page: 0 } });

  // Mutations
  const saveCallback = () => setSaved(true);

  const { mutate: createAccount } = useCreateAccount({
    onSuccessCallback: (data) => navigate(`/${ROUTE_PATHS.ACCOUNTS}/${data.uuid}`),
  });

  const { mutate: updateAccount } = useUpdateAccount({
    uuid: _.toString(uuid),
    onSuccessCallback: saveCallback,
  });

  const isCreateAccountMutation = useIsMutating({ mutationKey: [mutationKeys.createAccount] });
  const isUpdateAccountMutation = useIsMutating({ mutationKey: [mutationKeys.updateAccount] });

  const { mutate: archiveAccount } = useArchiveAccount({
    uuid: _.toString(uuid),
    onSuccessCallback: () => navigate(`/${ROUTE_PATHS.ACCOUNTS}`),
  });

  useEffect(() => {
    if (accountData) {
      setAccount(accountData);
      setAddresses(accountData.addresses || []);
    }
  }, [accountData]);

  // Permissions
  const { disabled, selfDisable } = useContext(DisableContext);
  selfDisable(!!account?.discarded_at);

  const employees = employeeData?.data || [];

  const handleCreateContact = () => {
    const params: ContactItemQueryParams = { account: uuid };
    navigate(`/${ROUTE_PATHS.CONTACTS}/new?${qs.stringify(params)}`);
  };

  const contactTableHeader = (
    <ResponsiveButton startIcon={<AddIcon />} onClick={handleCreateContact} variant="outlined">
      Add
    </ResponsiveButton>
  );

  const preferences = preferencesData ?? [];

  // Split options by key for each picker
  const districtOptions = useMemo(() => _.filter(preferences, { key: PREFERENCES_VALID_KEYS.ACCOUNT_SCHOOL_DISTRICT }), [preferences]);
  const statusOptions = useMemo(() => _.filter(preferences, { key: PREFERENCES_VALID_KEYS.ACCOUNT_STATUS }), [preferences]);
  const typeOptions = useMemo(() => _.filter(preferences, { key: PREFERENCES_VALID_KEYS.ACCOUNT_TYPE }), [preferences]);

  // Only now gate the UI with a boolean
  const showLoading = !account || isGetAccountByIdLoading;
  if (showLoading) return <Loading />;

  // Handlers
  const updateAccountData = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newAccount = { ...account, [event.target.id]: event.target.value };
    setAccount(newAccount as Account);
  };

  const handleSave = () => {
    if (!account) return;
    const payload: AccountSaveResponse = { account, addresses };
    if (isNew) createAccount(payload);
    else updateAccount(payload);
  };

  return (
    <Box sx={itemWrapperStyling}>
      <ItemHeader
        title={isNew ? 'Create Account' : (accountData?.name ?? '')}
        onSave={handleSave}
        archived={!!account.discarded_at}
        disableActions={Boolean(isCreateAccountMutation || isUpdateAccountMutation)}
        showMoreOptions={!isNew ? <MoreOptions onArchive={archiveAccount} name={account.name} itemName="account" /> : null}
        module={UserConfigurableModule.ACCOUNTS}
        infoItems={[
          { content: accountData?.type_value ?? '—' },
          {
            icon: <AccountCircleOutlined fontSize="small" color="action" />,
            content: accountData?.manager_display_name ?? '—',
          },
        ]}
      />

      {/* Left Side */}
      <Box sx={itemInnerLayoutStyling}>
        <Box sx={itemContainerStyling}>
          <AccordionSection title="Account Information" collapseId={UserSettingsCollapseKeys.ACCOUNT_ITEM_INFORMATION}>
            <Box sx={inputContainerStyling}>
              <Box sx={inputDividerStyling}>
                <TextField
                  id="name"
                  label="Name"
                  onChange={updateAccountData}
                  value={account?.name ?? ''}
                  {...errorPopulator(account, 'name')}
                  disabled={disabled}
                />
                <TextField
                  id="code"
                  label="Code"
                  onChange={updateAccountData}
                  value={account?.code ?? ''}
                  {...errorPopulator(account, 'code')}
                  disabled={disabled}
                />
                <TextFieldWithLink
                  linkType="phone"
                  id="phone"
                  label="Phone"
                  onChange={updateAccountData}
                  value={account?.phone ?? ''}
                  disabled={disabled}
                  {...errorPopulator(account, 'phone')}
                />
                <EmployeeSearchAutocomplete
                  isDisabled={disabled}
                  label="Account Manager"
                  error={errorPopulator(account, 'manager')}
                  value={uuidFinder(employees, account?.manager_uuid)}
                  onChange={(employee) => setAccount((prev) => (prev ? { ...prev, manager_uuid: employee?.uuid } : prev))}
                />
              </Box>

              <Box sx={inputDividerStyling}>
                {/* School District */}
                <PreferencePicker
                  label="School District"
                  options={districtOptions}
                  loading={isGetAllPreferencesFetching}
                  value={uuidFinder(districtOptions, account?.school_district_uuid) ?? null}
                  onValueChange={(v) => {
                    const pref = v as Preference | null;
                    setAccount((prev) => (prev ? { ...prev, school_district_uuid: pref?.uuid ?? undefined } : prev));
                  }}
                  disabled={disabled}
                  helperText={errorPopulator(account, 'school_district')?.helperText}
                />

                {/* Status */}
                <PreferencePicker
                  label="Status"
                  options={statusOptions}
                  loading={isGetAllPreferencesFetching}
                  value={uuidFinder(statusOptions, account?.status_uuid) ?? null}
                  onValueChange={(v) => {
                    const pref = v as Preference | null;
                    setAccount((prev) => (prev ? { ...prev, status_uuid: pref?.uuid ?? undefined } : prev));
                  }}
                  disabled={disabled}
                  helperText={errorPopulator(account, 'status')?.helperText}
                />

                {/* Type */}
                <PreferencePicker
                  label="Type"
                  options={typeOptions}
                  loading={isGetAllPreferencesFetching}
                  value={uuidFinder(typeOptions, account?.type_uuid) ?? null}
                  onValueChange={(v) => {
                    const pref = v as Preference | null;
                    setAccount((prev) => (prev ? { ...prev, type_uuid: pref?.uuid ?? undefined } : prev));
                  }}
                  disabled={disabled}
                  helperText={errorPopulator(account, 'type')?.helperText}
                />
              </Box>
            </Box>
          </AccordionSection>

          <AccountAddressTable addresses={addresses} onAddressesChange={setAddresses} />

          {!isNew && (
            <>
              <AccordionSection
                title="Contacts"
                collapseId={UserSettingsCollapseKeys.ACCOUNT_ITEM_CONTACTS}
                headerElements={contactTableHeader}
                hasData={(contactData?.data?.length ?? 0) > 0}
              >
                <AccountContactTable contacts={contactData?.data || []} />
              </AccordionSection>

              <EventsTimeline entityUuid={account.uuid} entityType="account" />

              <AccountTaskTable accountUuid={account.uuid} />
            </>
          )}

          <TagsSection tags={account.tags} collapseId={UserSettingsCollapseKeys.ACCOUNT_ITEM_TAGS} setTags={(tags) => setAccount({ ...account, tags })} />

          {!isNew && (
            <AttachmentsSection collapseId={UserSettingsCollapseKeys.ACCOUNT_ITEM_ATTACHMENTS} ownerType={AttachmentOwnerType.ACCOUNT} ownerUuid={uuid ?? ''} />
          )}

          <Snackbar open={saved} onClose={() => setSaved(false)} message="Saved!" />
        </Box>

        {/* Right Side */}
        {!isNew && (
          <Box sx={itemActivitySectionStyling}>
            <ActivityLogsSection ownerUuid={uuid} ownerType={ActivityLogOwnerType.ACCOUNT} collapseId={UserSettingsCollapseKeys.ACCOUNT_ITEM_ACTIVITY_LOG} />
            <NotesSection
              noteable={{ noteableUuid: uuid ?? '', noteableType: NoteableType.ACCOUNT }}
              collapseId={UserSettingsCollapseKeys.ACCOUNT_ITEM_NOTES}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default AccountItem;
